import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DEPARA_RECORDS_PAGE_SIZE,
  listConfigTableRecords,
  type ActivityTablesSummary,
} from "@/api/configuration-server";
import { DeParaRecordsTable } from "@/components/agents/configuration-server/DeParaRecordsTable";
import { countryName, countrySummary } from "@/lib/country-names";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Cores do mock Configurations v2.dc.html (bloco modal.tableTabs).
const TAB_ON = "border-b-[3px] border-[#ffc83d] bg-white text-[#08254c]";
const TAB_OFF = "border-b-[3px] border-transparent text-[#596b85] hover:bg-white";
const PILL_ON = "bg-[rgba(255,200,61,0.18)] text-[#8a6100]";
const PILL_OFF = "bg-[#eef1f6] text-[#596b85]";
/** A 4ª coluna da grade, nas duas linhas: o scope item (Execução) ou a contagem de scope items (Gerenciamento). */
const FOURTH_COLUMN = "sm:col-start-4 sm:row-span-2 sm:row-start-1";
const META_LABEL =
  "font-mono text-[10px] uppercase tracking-[0.12em] text-[#596b85]";
const PAGER_BUTTON =
  "h-7 rounded-md border border-[#e8ebf0] bg-white px-3 font-mono text-[12px] text-[#2c3e5b] " +
  "transition-colors enabled:hover:bg-[rgba(255,200,61,0.1)] enabled:hover:text-[#08254c] " +
  "disabled:cursor-not-allowed disabled:text-[#b2bccb]";

export function ConfigActivityModal({
  open,
  onClose,
  projectId,
  artifactId,
  activityCode,
  codeLabel,
  queryKey,
  loadTables,
  showScope,
  meta,
  scopeItems,
  errorLabels,
}: {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  artifactId?: string;
  activityCode: string;
  codeLabel: string;
  queryKey: string;
  loadTables: (
    projectId: string,
    activityCode: string,
    artifactId?: string,
  ) => Promise<Omit<ActivityTablesSummary, "tables"> & {
    tables: Array<Omit<ActivityTablesSummary["tables"][number], "status"> & { status: string }>;
  }>;
  showScope: boolean;
  meta?: { name: string | null; priority: string | null };
  /** Execução: os scope items da configuração (código e nome), o 8º campo. Sem ele, o campo não existe. */
  scopeItems?: { id: string; name: string | null }[];
  errorLabels?: { tables: string; records: string; retry: string };
}) {
  const { t, i18n } = useTranslation();
  const [tableIndex, setTableIndex] = useState(0);
  const [page, setPage] = useState(1);
  const numberFormat = new Intl.NumberFormat(i18n.language || "en-US");

  useEffect(() => {
    setTableIndex(0);
    setPage(1);
  }, [activityCode, open, projectId, artifactId, queryKey]);

  const enabled = open && Boolean(projectId);

  const { data: activity, isLoading, isError: tablesError, refetch: retryTables } = useQuery({
    queryKey: [`${queryKey}-tables`, projectId, artifactId, activityCode],
    queryFn: () => loadTables(projectId as string, activityCode, artifactId),
    enabled,
  });

  const tables = tablesError ? [] : activity?.tables ?? [];
  const table = tables[Math.min(tableIndex, Math.max(0, tables.length - 1))];

  const { data: records, isLoading: recordsLoading, isError: recordsError, refetch: retryRecords } = useQuery({
    queryKey: [
      `${queryKey}-records`,
      projectId,
      artifactId,
      activityCode,
      table?.domain,
      table?.table_name,
      page,
    ],
    queryFn: () =>
      listConfigTableRecords(projectId as string, table!.table_name, {
        domain: table!.domain,
        artifact_id: artifactId,
        page,
        // Sem isto, o modal de um SSCUI mostraria as colunas de outro que
        // compartilha a mesma tabela extraída.
        activity_code: activityCode,
      }),
    enabled: enabled && Boolean(table),
    placeholderData: (previous, previousQuery) => {
      const provenance = [
        `${queryKey}-records`, projectId, artifactId, activityCode,
        table?.domain, table?.table_name,
      ];
      return provenance.every((value, index) => previousQuery?.queryKey[index] === value)
        ? previous : undefined;
    },
  });

  const totalPages = Math.max(
    1,
    Math.ceil((records?.total ?? 0) / DEPARA_RECORDS_PAGE_SIZE),
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-[1180px]">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-2.5 text-[15px]">
            <span className="font-mono font-semibold text-[#08254c]">
              {activity?.activity_code ?? activityCode}
            </span>
            <span className="font-semibold text-[#08254c]">{(meta ? meta.name : activity?.name) ?? "—"}</span>
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#596b85]">
            {t(
              "Extracted records for this activity, pivoted with fields as columns. One tab per source table.",
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Três colunas de campos (3 + 3) e a 4ª para o scope item, que ocupa as duas linhas; sem a lista, o Scope (contagem) ocupa a 4ª. */}
        <div
          data-testid="activity-meta"
          className="grid grid-cols-2 gap-4 border-y border-[#e8ebf0] bg-[#fbfcfd] px-3 py-3 text-[13px] sm:grid-cols-4"
        >
          <div>
            <div className={META_LABEL}>{codeLabel}</div>
            <div className="font-mono text-[#08254c]">{activityCode}</div>
          </div>
          <div>
            <div className={META_LABEL}>{t("Activity")}</div>
            <div className="text-[#0d1b2e]">{(meta ? meta.name : activity?.name) ?? "—"}</div>
          </div>
          <div>
            <div className={META_LABEL}>{t("Priority")}</div>
            <div className="text-[#0d1b2e]">{(meta ? meta.priority : activity?.priority) ?? "—"}</div>
          </div>
          {showScope && (
            <div>
              <div className={META_LABEL}>{t("Country/Region")}</div>
              <div
                className="text-[#0d1b2e]"
                title={activity?.countries?.length ? activity.countries.map((code) => countryName(code, i18n.language)).join(", ") : undefined}
              >
                {!tablesError && activity
                  ? activity.countries?.length ? countrySummary(activity.countries, i18n.language) : t("Global")
                  : "—"}
              </div>
            </div>
          )}
          {showScope && (
            <div>
              <div className={META_LABEL}>{t("Subarea")}</div>
              <div className="text-[#0d1b2e]">{(!tablesError && activity?.application_subarea) || "—"}</div>
            </div>
          )}
          <div>
            <div className={META_LABEL}>{t("Fields")}</div>
            <div className="text-[#0d1b2e]">
              {!tablesError && activity ? t("{{tables}} tables · {{fields}} fields", {
                tables: tables.length,
                fields: table?.field_count ?? 0,
              }) : "—"}
            </div>
          </div>
          {scopeItems ? (
            <div className={FOURTH_COLUMN}>
              <div className={META_LABEL}>{t("Scope item")}</div>
              <ScopeItemList items={scopeItems} />
            </div>
          ) : showScope && (
            <div className={FOURTH_COLUMN}>
              <div className={META_LABEL}>{t("Scope")}</div>
              <div className="text-[#0d1b2e]">
                {/* Sem a quantidade (App Fiori aberto sem a linha), "—" e não um zero inventado. */}
                {!tablesError && activity && activity.scope_item_count !== undefined ? t("{{count}} approved scope items", {
                  count: activity.scope_item_count,
                }) : "—"}
              </div>
            </div>
          )}
        </div>

        <div role="tablist" className="flex overflow-x-auto border-b border-[#e8ebf0] bg-[#fbfcfd]">
          {tables.map((item, index) => {
            const on = index === tableIndex;
            return (
              <button
                key={item.table_name}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  setTableIndex(index);
                  setPage(1);
                }}
                className={`flex flex-none items-center gap-2 px-4 py-3 ${on ? TAB_ON : TAB_OFF}`}
              >
                <span className="font-mono text-[13px] font-semibold">{item.table_name}</span>
                <span
                  className={`rounded-full px-2 py-0.5 font-mono text-[11px] ${on ? PILL_ON : PILL_OFF}`}
                >
                  {numberFormat.format(item.record_count)}
                </span>
              </button>
            );
          })}
        </div>

        {tablesError || recordsError ? (
          <div role="alert" className="py-8 text-center text-sm text-red-700">
            <p>{tablesError ? t(errorLabels?.tables ?? "Could not load the extracted tables.") : t(errorLabels?.records ?? "Could not load the extracted records.")}</p>
            <Button variant="outline" onClick={() => void (tablesError ? retryTables() : retryRecords())}>{t(errorLabels?.retry ?? "Try again")}</Button>
          </div>
        ) : isLoading || recordsLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-[11px] text-[#7b8799]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("Loading extracted values…")}
          </div>
        ) : tables.length === 0 ? (
          <div className="py-12 text-center text-[13px] text-[#596b85]">
            {t("No extracted table carries this activity.")}
          </div>
        ) : (
          <DeParaRecordsTable
            columns={records?.columns ?? []}
            records={records?.records ?? []}
          />
        )}

        {tables.length > 0 && !tablesError && !recordsError && !recordsLoading && (
          <div className="flex items-center justify-between gap-3 border-t border-[#e8ebf0] bg-[#fbfcfd] px-3 py-3">
            <span className="font-mono text-[11px] text-[#596b85]">
              {t("Page {{page}} of {{totalPages}}", { page, totalPages })} ·{" "}
              {numberFormat.format(records?.total ?? 0)} {t("Records")}
            </span>
            <div className="flex gap-1.5">
              <Button
                type="button"
                variant="outline"
                className={PAGER_BUTTON}
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {t("Prev")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className={PAGER_BUTTON}
                disabled={page >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                {t("Next")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Até dois scope items à vista; com mais, "+N more" abre a lista inteira (rolável). */
const SCOPE_ITEMS_SHOWN = 2;

function ScopeItemList({ items }: { items: { id: string; name: string | null }[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return <div className="text-[#0d1b2e]">—</div>;
  const hidden = items.length - SCOPE_ITEMS_SHOWN;
  const shown = expanded || hidden <= 0 ? items : items.slice(0, SCOPE_ITEMS_SHOWN);
  return (
    <div className="text-[#0d1b2e]">
      <ul className={expanded ? "max-h-40 overflow-y-auto" : undefined}>
        {shown.map((item) => (
          <li key={item.id}>{item.name ? `${item.id} ${item.name}` : item.id}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="mt-0.5 text-xs font-semibold text-[#1d4ea0] underline-offset-2 hover:underline"
        >
          {expanded ? t("Show less") : t("+{{count}} more", { count: hidden })}
        </button>
      )}
    </div>
  );
}
